import { Anchor, Button, Container, Group, Paper, PasswordInput, Stack, Text, TextInput, Title } from "@mantine/core";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { ApiError, api, auth, type MeResponse } from "../lib/api";

export function LoginScreen({ onLoggedIn }: { onLoggedIn: (me: MeResponse) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const resp = await api.login(email, password);
      if (!resp.tokens) {
        if (!resp.partial_token) {
          setError("שגיאה בלתי-צפויה. נסה שוב.");
          return;
        }
        // Hand off to the 2FA verify screen with the partial token in state.
        navigate("/2fa/verify", { state: { partialToken: resp.partial_token } });
        return;
      }
      auth.setTokens(resp.tokens);
      const me = await api.me();
      onLoggedIn(me);
    } catch (err) {
      if (err instanceof ApiError) {
        const msg = mapLoginError(err.detail);
        setError(msg);
      } else {
        setError("משהו השתבש. נסה שוב.");
      }
    } finally {
      setBusy(false);
    }
  };

  function mapLoginError(detail: string): string {
    switch (detail) {
      case "invalid_credentials":
        return "פרטי כניסה שגויים.";
      case "signup_needs_email_verification":
        return "כתובת הדוא\"ל שלך עדיין לא אומתה. בדוק/י את תיבת הדואר ולחץ/י על קישור האימות שנשלח אליך.";
      case "signup_awaiting_operator_approval":
        return "ההרשמה שלך אושרה והדוא\"ל אומת, אבל החשבון עדיין ממתין לאישור של מפעיל המערכת.";
      case "signup_rejected":
        return "ההרשמה שלך נדחתה. אם זו טעות, פנה/י למפעיל המערכת.";
      case "login_rate_limited":
        return "יותר מדי ניסיונות. נסה/י שוב בעוד דקה.";
      default:
        return "משהו השתבש. נסה שוב.";
    }
  }

  return (
    <Container size="xs" py="xl">
      <Paper p="lg" radius="md" withBorder>
        <Stack>
          <Title order={2}>כניסה</Title>
          <Text c="dimmed" size="sm">דשבורד רו"ח דובאך</Text>
          <form onSubmit={handle}>
            <Stack>
              <TextInput
                label="אימייל"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
                dir="ltr"
              />
              <PasswordInput
                label="סיסמה"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                dir="ltr"
              />
              {error && (
                <Text c="red" size="sm">
                  {error}
                </Text>
              )}
              <Button type="submit" loading={busy} fullWidth>
                כניסה
              </Button>
              <Group justify="space-between">
                <Anchor component={Link} to="/signup" size="sm">
                  אין לי חשבון — הרשמה
                </Anchor>
                <Anchor component={Link} to="/forgot-password" size="sm">
                  שכחת סיסמה?
                </Anchor>
              </Group>
            </Stack>
          </form>
        </Stack>
      </Paper>
    </Container>
  );
}
